import React, { useState, useEffect, useCallback } from "react";
import COLORS from "../../constants/colors";
import HeartView from "../common/HeartView";
import FlowTopBar from "../common/FlowTopBar";
import {
  detailPanelStyle,
  detailTagStyle,
  heroWordStyle,
  mainCardStyle,
  miniBtnStyle,
  pageStyle,
} from "../../styles/commonStyles";
import { speakFrench } from "../../utils/speech";
import { fetchWithAuth, API_ENDPOINTS, getStaticUrl, wordApi } from "../../utils/api";

export default function WordDetailPage({
  word,
  heartCount = 0,
  detailTab,
  tempNote,
  meaningExampleIndex,
  onSetDetailTab,
  onSetTempNote,
  onSetMeaningExampleIndex,
  onSaveNote,
  onNext,
  onMarkWrong,
  onPrev,
  onStar,
  onSkip,
  onHome,
  disablePrev = false,
}) {
  // 图片状态：仅使用后端AI生成的图片
  const [imageUrl, setImageUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // 🔥 修复1：用 useCallback 缓存函数，保证 useEffect 能正常触发
  // 调用豆包AI生成单词配图
  const generateWordImage = useCallback(async (force = false) => {
    if (!word) return;
    
    setIsGenerating(true);
    setErrorMsg(null);
    try {
      console.log(`为单词 "${word.word}" 调用豆包AI生成图片...`);
      
      // 修改：使用传入的 force 参数，默认 false，利用缓存
      const endpoint = `${API_ENDPOINTS.GENERATE_WORD_IMAGE(word.id)}?force=${force}`;
      
      const res = await fetchWithAuth(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (!res || !res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP ${res?.status}: ${errorText}`);
      }
      
      const data = await res.json();
      console.log(`豆包AI图片生成成功:`, data);
      
      const fullUrl = getStaticUrl(data.image_url);
      setImageUrl(fullUrl);
    } catch (err) {
      console.error("豆包AI图片生成失败：", err);
      setErrorMsg(`图片生成失败: ${err.message}`);
      setImageUrl(null);
    } finally {
      setIsGenerating(false);
    }
  }, [word]);

  // 单词切换时自动生成图片
  useEffect(() => {
    if (!word?.id) return;

    console.log("单词切换，检查是否需要生成图片 ID:", word.id);
    setImageUrl(null);
    setErrorMsg(null);
    // 修改：使用 false，利用缓存，只生成一次
    generateWordImage(false);

  }, [word?.id, generateWordImage]);

  if (!word) return null;
  const meanings = word.common_meanings || [];

  return (
    <div style={pageStyle()}>
      <div style={mainCardStyle()}>
        <FlowTopBar
          title="法语单词界面"
          onPrev={onPrev}
          onStar={onStar}
          onSkip={onSkip}
          onHome={onHome}
          disablePrev={disablePrev}
        />

        {/* 单词 + 爱心 */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.7rem",
            flexWrap: "wrap",
          }}
        >
          <div style={heroWordStyle(0)} onClick={() => speakFrench(word.word)}>
            {word.word}
          </div>
          <HeartView count={heartCount} />
        </div>

        {/* 音标 */}
        <div
          onClick={() => speakFrench(word.word)}
          style={{
            textAlign: "center",
            color: COLORS.subtext,
            cursor: "pointer",
            marginTop: "0.25rem",
          }}
        >
          {word.phonetic} 🔊
        </div>

        {/* 词性 */}
        <div
          style={{
            textAlign: "center",
            marginTop: "0.85rem",
            color: COLORS.text,
            fontWeight: 600,
          }}
        >
          {word.type} · {word.translation}
        </div>

        {/* AI 生图区域 */}
        <div style={{ 
          marginTop: "1rem", 
          borderRadius: "16px", 
          overflow: "hidden", 
          position: "relative",
          minHeight: "180px",
          background: "#f5f5f5"
        }}>
          {imageUrl && !isGenerating && (
            <img
              src={imageUrl}
              alt={word.word}
              style={{ 
                width: "100%", 
                height: "180px", 
                objectFit: "contain", 
                display: "block" 
              }}
              onError={(e) => {
                console.error("图片加载失败");
                setErrorMsg("图片加载失败，请重试");
                setImageUrl(null);
              }}
            />
          )}

          {isGenerating && (
            <div style={{
              position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
              background: "rgba(255,255,255,0.9)", 
              color: "#333", 
              display: "flex", 
              flexDirection: "column",
              alignItems: "center", 
              justifyContent: "center",
              fontSize: "18px",
              fontWeight: "600"
            }}>
              <div>🤖 豆包AI正在生成单词配图...</div>
              <div style={{fontSize:"14px", color:"#666", marginTop:"8px"}}>请稍候，约3-10秒</div>
            </div>
          )}

          {errorMsg && !isGenerating && (
            <div style={{
              position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
              background: "#fff5f5", 
              color: "#e53e3e", 
              display: "flex", 
              flexDirection: "column",
              alignItems: "center", 
              justifyContent: "center",
              padding: "1rem",
              textAlign: "center"
            }}>
              <div style={{fontWeight:"600", marginBottom:"0.5rem"}}>❌ {errorMsg}</div>
              <button
                onClick={() => generateWordImage(true)}
                style={{
                  marginTop: "0.5rem",
                  padding: "0.5rem 1rem",
                  borderRadius: "8px",
                  border: "none",
                  background: COLORS.green,
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: "600"
                }}
              >
                 重新生成
              </button>
            </div>
          )}

          {!imageUrl && !isGenerating && !errorMsg && (
            <div style={{
              position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              color: "#999"
            }}>
              点击「重新生成」获取AI配图
            </div>
          )}
        </div>

        {/* meanings 列表 */}
        <div style={{ marginTop: "1rem" }}>
          {meanings.map((meaning, idx) => (
            <div
              key={idx}
              style={{
                padding: "0.85rem 0",
                borderBottom: "1px dashed #D8DADF",
                cursor: "pointer",
              }}
              onClick={() =>
                onSetMeaningExampleIndex(meaningExampleIndex === idx ? null : idx)
              }
            >
              <div style={{ fontWeight: 600, color: COLORS.text }}>{meaning}</div>
              <div
                style={{
                  color: COLORS.subtext,
                  fontSize: "0.88rem",
                  marginTop: "0.3rem",
                }}
              >
                点击查看对应例句与翻译
              </div>
              {meaningExampleIndex === idx && (
                <div
                  style={{
                    marginTop: "0.6rem",
                    color: COLORS.subtext,
                    lineHeight: 1.8,
                  }}
                >
                  <div>
                    <strong style={{ color: COLORS.text }}>{word.example}</strong>
                  </div>
                  <div>{word.example_translation}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 标签页 */}
        <div
          style={{
            background: COLORS.soft,
            borderRadius: "18px",
            padding: "1rem",
            marginTop: "1.2rem",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              flexWrap: "wrap",
              marginBottom: "1rem",
            }}
          >
            <button
              onClick={() => onSetDetailTab("usage")}
              style={detailTagStyle(detailTab === "usage", COLORS.green)}
            >
              用法
            </button>
            <button
              onClick={() => onSetDetailTab("derivative")}
              style={detailTagStyle(detailTab === "derivative", COLORS.pink)}
            >
              派生词
            </button>
            <button
              onClick={() => onSetDetailTab("syn")}
              style={detailTagStyle(detailTab === "syn", COLORS.orange)}
            >
              同/反义词
            </button>
            <button
              onClick={() => onSetDetailTab("note")}
              style={detailTagStyle(detailTab === "note", COLORS.lightBlue)}
            >
              笔记
            </button>
          </div>

          {detailTab === "usage" && (
            <div style={detailPanelStyle()}>
              {word.usages?.length ? (
                word.usages.map((u, i) => (
                  <div key={i} style={{ marginBottom: "1rem" }}>
                    <div style={{ fontWeight: 700, color: COLORS.text }}>{u.title}</div>
                    <div style={{ color: COLORS.subtext, marginTop: "0.35rem" }}>
                      {u.meaning}
                    </div>
                    <div style={{ marginTop: "0.45rem", lineHeight: 1.7 }}>
                      <strong>{u.example}</strong>
                      <div style={{ color: COLORS.subtext }}>{u.translation}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ color: COLORS.subtext }}>
                  暂无用法数据
                </div>
              )}
            </div>
          )}

          {detailTab === "derivative" && (
            <div style={detailPanelStyle()}>
              {word.derivatives?.length ? (
                word.derivatives.map((d, i) => (
                  <div key={i} style={{ marginBottom: "1rem" }}>
                    <div style={{ fontWeight: 700, color: COLORS.text }}>
                      {d.word} · {d.type}
                    </div>
                    <div style={{ color: COLORS.subtext, marginTop: "0.35rem" }}>
                      {d.meaning}
                    </div>
                    <div style={{ marginTop: "0.35rem" }}>最常用法：{d.usage}</div>
                    <div style={{ marginTop: "0.35rem", color: COLORS.subtext }}>
                      {d.example}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ color: COLORS.subtext }}>
                  暂无派生词数据
                </div>
              )}
            </div>
          )}

          {detailTab === "syn" && (
            <div style={detailPanelStyle()}>
              {word.synonyms_antonyms?.length ? (
                word.synonyms_antonyms.map((s, i) => (
                  <div key={i} style={{ marginBottom: "1rem" }}>
                    <div style={{ fontWeight: 700, color: COLORS.text }}>
                      [{s.relation}] {s.word} · {s.type}
                    </div>
                    <div style={{ color: COLORS.subtext, marginTop: "0.35rem" }}>
                      {s.meaning}
                    </div>
                    <div style={{ marginTop: "0.35rem", color: COLORS.subtext }}>
                      {s.example}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ color: COLORS.subtext }}>
                  暂无同反义词数据
                </div>
              )}
            </div>
          )}

          {detailTab === "note" && (
            <div style={detailPanelStyle()}>
              <textarea
                value={tempNote}
                onChange={(e) => onSetTempNote(e.target.value)}
                placeholder="写下你对这个单词的任何想法..."
                style={{
                  width: "100%",
                  minHeight: "140px",
                  borderRadius: "14px",
                  border: `1px solid ${COLORS.line}`,
                  padding: "1rem",
                  resize: "vertical",
                  fontSize: "0.95rem",
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={onSaveNote}
                style={{
                  ...miniBtnStyle(),
                  width: "100%",
                  marginTop: "0.8rem",
                  borderRadius: "14px",
                  padding: "0.9rem 1rem",
                }}
              >
                保存笔记
              </button>
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
          <button
            onClick={onNext}
            style={{
              flex: 1,
              padding: "0.95rem 0",
              borderRadius: "14px",
              border: `2px solid ${COLORS.green}`,
              background: "#f0fff4",
              color: COLORS.text,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            下一个
          </button>
          <button
            onClick={onMarkWrong}
            style={{
              flex: 1,
              padding: "0.95rem 0",
              borderRadius: "14px",
              border: `2px solid ${COLORS.red}`,
              background: "#fff5f5",
              color: COLORS.text,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            记错了
          </button>
        </div>
      </div>
    </div>
  );
}